import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import P from '@deliveroo/consumer-component-library/components/P';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import RestaurantReviewsCard from '../../components/RestaurantReviewsCard';
import ResponsiveGridLayout from '../../components/ResponsiveGridLayout';
import InterpolateLinks from '../../components/InterpolateLinks';
import CityFilter from '../../components/CityFilter';
import { restaurantReviewsSelector } from '../../selectors/cuisineSelectors';
import i18n from '../../utils/i18n';
import scrollTo from '../../utils/scrollTo';

import styles from './styles.scss';

const INITIAL_MAX_REVIEWS = 3;
// if no city filter is selected, we don't want to show ~60 reviews
const MAX_REVIEWS_WHEN_NO_CITY_FILTER_SELECTED = 9;

export class RestaurantReviewsContainer extends React.Component {
  state = {
    hasMounted: false,
    isExpanded: false,
    selectedCity: null,
    selectedCityIndex: null,
  };

  /**
   * Allow us to set CSS properties for some elements only after the page has
   * initially mounted.
   */
  componentDidMount() {
    this.setState({ hasMounted: true });
  }

  getLocationName = () => {
    const { city, tld } = this.props;

    if (city) return city;

    return i18n.gettext(`landingPage.cuisine.restaurantReviews.header.location.${tld}`);
  };

  onSelectCity = (selectedCity = {}, selectedCityIndex) => () => {
    if (
      this.state.selectedCity &&
      this.state.selectedCity.compareValue === selectedCity.compareValue
    ) {
      // toggle city, if already selected.
      // also set isExpanded: false so the user can
      // choose to click 'view more' if they so wish
      this.setState({
        selectedCity: null,
        selectedCityIndex: null,
        isExpanded: false,
      });
    } else {
      this.setState({
        selectedCity,
        selectedCityIndex,
        isExpanded: false,
      });
    }
  };

  onClickMoreCities = () => {
    const targetNode = document.querySelector('#choose-city');
    scrollTo(targetNode);
  };

  onClickCta = () => {
    const targetNode = document.querySelector('#location-search');
    scrollTo(targetNode);
  };

  onClickChooseNeighbourhood = (event) => {
    event.preventDefault();
    const targetNode = document.querySelector('#choose-neighbourhood');
    scrollTo(targetNode);
    return false;
  };

  onClickViewAllReviews = () => {
    this.setState({ isExpanded: true });
  };

  renderReviews = (filteredRestaurantReviews) => {
    const { restaurantReviews } = this.props;
    const { isExpanded, hasMounted } = this.state;

    const maxVisibleReviews = isExpanded ? restaurantReviews.length : INITIAL_MAX_REVIEWS;

    return (
      <Spacer adjacentVerticalSpacing="medium">
        <ResponsiveGridLayout gutter="large" maxColumns={3} minColumnWidth={260}>
          {filteredRestaurantReviews.map(({ reviews, restaurant }, index) => {
            const card = (
              <RestaurantReviewsCard
                key={`review-${restaurant.id}`}
                reviews={reviews}
                readMoreLinkText={i18n.gettext(
                  'landingPage.cuisine.restaurantReviews.seeMore.linkText',
                )}
                imageUrl={restaurant.image}
                restaurantName={restaurant.name}
                restaurantLink={restaurant.url}
                rating={{
                  value: restaurant.averageRating,
                  count: restaurant.numRatings,
                }}
              />
            );

            if (!hasMounted || index < maxVisibleReviews) return card;

            return (
              <div className={styles.hidden} key={`review-${restaurant.id}`}>
                {card}
              </div>
            );
          })}
        </ResponsiveGridLayout>
      </Spacer>
    );
  };

  render() {
    const { cuisine, city, restaurantReviews, isPostcodeCountry, cityFilterOptions } = this.props;
    const { isExpanded, hasMounted, selectedCity, selectedCityIndex } = this.state;

    const filteredRestaurantReviews = selectedCity
      ? restaurantReviews.filter(({ restaurant }) => restaurant.city === selectedCity.compareValue)
      : restaurantReviews.slice(0, MAX_REVIEWS_WHEN_NO_CITY_FILTER_SELECTED);

    const canExpand =
      hasMounted && !isExpanded && filteredRestaurantReviews.length > INITIAL_MAX_REVIEWS;

    const locationPrompt = (
      <div className={styles.locationPrompt}>
        <P color="grey">
          <InterpolateLinks
            template={i18n.gettext(
              `landingPage.cuisine.restaurantReviews.header.chooseNeighbourhood.${
                isPostcodeCountry ? 'postcode' : 'address'
              }(%(cuisine)s,%(linkHref)s)`,
              { cuisine, linkHref: '%(linkHref)s' },
            )}
            render={({ linkText }) => (
              <a href="#choose-neighbourhood" onClick={this.onClickChooseNeighbourhood}>
                {linkText}
              </a>
            )}
          />
        </P>
      </div>
    );

    const cityFilters = (
      <CityFilter
        cities={cityFilterOptions}
        onSelectItem={this.onSelectCity}
        selectedIndex={selectedCityIndex}
        label={i18n.gettext('landingPage.cuisine.cityFilter.label.default')}
        mobileLabel={i18n.gettext('landingPage.cuisine.cityFilter.label.short')}
        viewMoreLabel={i18n.gettext('landingPage.cuisine.cityFilter.moreCities.linkText')}
        onViewMore={this.onClickMoreCities}
      />
    );

    const viewMore = (
      <div className={styles.restaurantReviewsButton}>
        <Button type="secondary" size="small" onClick={this.onClickViewAllReviews}>
          {i18n.gettext('landingPage.cuisine.restaurantReviews.cta.moreReviews(%(length)s)', {
            length: filteredRestaurantReviews.length - INITIAL_MAX_REVIEWS,
          })}
        </Button>
      </div>
    );

    const cta = (
      <div className={styles.restaurantReviewsButton}>
        <Button size="medium" onClick={this.onClickCta}>
          {i18n.gettext('landingPage.cuisine.restaurantReviews.cta.orderFrom')}
        </Button>
      </div>
    );

    if (restaurantReviews.length === 0) {
      return null;
    }

    return (
      <Spacer spacing="x-large 0">
        <Heading color="black" level={2} size="x-small">
          <span className={styles.heading}>
            {i18n.gettext(
              'landingPage.cuisine.restaurantReviews.header.text(%(cuisine)s,%(location)s)',
              { cuisine, location: this.getLocationName() },
            )}
          </span>
        </Heading>
        {city ? locationPrompt : cityFilters}
        {this.renderReviews(filteredRestaurantReviews)}
        {canExpand ? viewMore : cta}
      </Spacer>
    );
  }
}

RestaurantReviewsContainer.propTypes = {
  restaurantReviews: PropTypes.arrayOf(
    PropTypes.shape({
      restaurant: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        numRatings: PropTypes.string.isRequired,
        averageRating: PropTypes.number.isRequired,
        city: PropTypes.string.isRequired,
      }).isRequired,
      reviews: PropTypes.arrayOf(
        PropTypes.shape({
          rating: PropTypes.number.isRequired,
          content: PropTypes.string.isRequired,
          reviewerName: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ),
  cuisine: PropTypes.string.isRequired,
  city: PropTypes.string,
  tld: PropTypes.string.isRequired,
  isPostcodeCountry: PropTypes.bool,
  cityFilterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      compareValue: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

RestaurantReviewsContainer.defaultProps = {
  restaurantReviews: [],
  city: undefined,
  isPostcodeCountry: false,
};

export default connect(restaurantReviewsSelector)(RestaurantReviewsContainer);
